import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchApi } from '../../utilities/api';
import { ENDPOINTS } from '../../utilities/endpoints';

interface RawPricePoint {
  propertyId: string;
  buildingId: string;
  bidAmount: number;
  insertDate: number;
  bidId: string;
}

export interface PricePoint {
  month: string;
  price: number;
}

interface PriceState {
  priceHistory: PricePoint[];
  isFetching: boolean;
  fetchError: string | null;
}

const initialState: PriceState = {
  priceHistory: [],
  isFetching: false,
  fetchError: null,
};


export const fetchPriceHistory = createAsyncThunk<
  PricePoint[],
  string,
  { rejectValue: string }
>(
  'price/fetchPriceHistory',
  async (buildingId, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    if (!token) {
      return rejectWithValue('No auth token found');
    }
    try {
      const response = await fetchApi<{ totalCount: number; priceHistory: RawPricePoint[] }>(
        ENDPOINTS.PROPERTIES.PRICE_HISTORY(buildingId),
        { method: 'GET', headers: { Authorization: token, Accept: 'application/json' } }
      );
      return response.data.priceHistory.map((p) => ({
        month: new Date(p.insertDate * 1000).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        price: p.bidAmount,
      }));
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to load price history');
    }
  }
);

const priceSlice = createSlice({
  name: 'price',
  initialState,
  reducers: {
    clearPriceHistory(state) {
      state.priceHistory = [];
      state.fetchError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPriceHistory.pending, (state) => {
        state.isFetching = true;
        state.fetchError = null;
      })
      .addCase(
        fetchPriceHistory.fulfilled,
        (state, action: PayloadAction<PricePoint[]>) => {
          state.isFetching = false;
          state.priceHistory = action.payload;
        }
      )
      .addCase(fetchPriceHistory.rejected, (state, action) => {
        state.isFetching = false;
        state.fetchError =
          action.payload ?? action.error.message ?? 'Unknown error';
      });
  },
});

export const { clearPriceHistory } = priceSlice.actions;
export default priceSlice.reducer;
